import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ShieldAlert, MessageCircleWarning } from "lucide-react";
import { toast } from "sonner";

/**
 * Unified purge-or-request dialog.
 *
 * - super_admin sees the classic "Permanently delete" confirmation; clicking
 *   the destructive CTA hits ``onConfirmPurge()`` (the parent's existing
 *   purge handler).
 * - everyone else (admin, sme, instructor) sees a "Request super admin to
 *   delete" form with a reason textarea. Submitting POSTs to
 *   ``/admin/delete-requests`` and toasts a confirmation.
 *
 * Props
 * -----
 * - open / onOpenChange: controlled dialog state from parent.
 * - resourceType: "user" | "program"
 * - resourceId: string
 * - resourceLabel: friendly name shown in dialog text.
 * - onConfirmPurge: async () => void — used only when role==="super_admin".
 *   Parent supplies its existing purge implementation (cascades, toasts, list
 *   refresh) so we don't duplicate that logic here.
 * - confirmTestId / cancelTestId: optional data-testids for e2e tests.
 */
export default function RequestPurgeDialog({
    open,
    onOpenChange,
    resourceType,
    resourceId,
    resourceLabel,
    onConfirmPurge,
    confirmTestId = "purge-confirm",
    cancelTestId = "purge-cancel",
}) {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";

    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setReason("");
        setSubmitting(false);
    };

    const handleSubmitRequest = async () => {
        if (reason.trim().length < 3) {
            toast.error("Please add a short reason (at least 3 characters).");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/delete-requests", {
                resource_type: resourceType,
                resource_id: resourceId,
                resource_label: resourceLabel,
                reason: reason.trim(),
            });
            if (data?.duplicate) {
                toast.info("A delete request is already open for this record — the super admin has been notified.");
            } else {
                toast.success("Request sent to the super admin. You'll be notified once it's reviewed.");
            }
            onOpenChange?.(false);
            reset();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not send the request. Try again.";
            toast.error(typeof msg === "string" ? msg : "Request failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSuperPurge = async () => {
        if (!onConfirmPurge) return;
        setSubmitting(true);
        try {
            await onConfirmPurge();
            onOpenChange?.(false);
            reset();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AlertDialog
            open={open}
            onOpenChange={(o) => {
                onOpenChange?.(o);
                if (!o) reset();
            }}
        >
            <AlertDialogContent data-testid="purge-or-request-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        {isSuper ? (
                            <ShieldAlert className="h-5 w-5 text-destructive" />
                        ) : (
                            <MessageCircleWarning className="h-5 w-5 text-accent" />
                        )}
                        {isSuper
                            ? "Permanently delete this record?"
                            : "Request super admin to delete"}
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div className="space-y-3 text-sm text-muted-foreground">
                            {isSuper ? (
                                <p>
                                    This will permanently purge{" "}
                                    <span className="font-medium text-foreground">
                                        {resourceLabel || resourceId}
                                    </span>{" "}
                                    and all related data. This cannot be undone.
                                </p>
                            ) : (
                                <>
                                    <p>
                                        Only the super admin can permanently delete records. Send your
                                        request below — include a short note explaining why{" "}
                                        <span className="font-medium text-foreground">
                                            {resourceLabel || resourceId}
                                        </span>{" "}
                                        should be purged.
                                    </p>
                                    <Textarea
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="e.g. Duplicate account created by mistake during bulk import on Feb 14."
                                        rows={4}
                                        className="text-foreground"
                                        data-testid="purge-request-reason"
                                        disabled={submitting}
                                    />
                                    <p className="text-[11px]">
                                        The super admin will be notified by email-style chat and can
                                        approve or decline directly from their Delete Requests inbox.
                                    </p>
                                </>
                            )}
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel
                        disabled={submitting}
                        data-testid={cancelTestId}
                    >
                        Cancel
                    </AlertDialogCancel>
                    {isSuper ? (
                        <AlertDialogAction
                            onClick={handleSuperPurge}
                            disabled={submitting}
                            data-testid={confirmTestId}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                            ) : null}
                            Permanently delete
                        </AlertDialogAction>
                    ) : (
                        <Button
                            onClick={handleSubmitRequest}
                            disabled={submitting || reason.trim().length < 3}
                            data-testid="purge-request-submit"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                            ) : null}
                            Send request
                        </Button>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
